/*
 * We want to be able to render only bars/parts of line that are visible to users,
 * but without introducing visual artifacts like bars missing in front of users while scrolling
 * or lines that start in middle of chart for sparse intraday stock data.
 *
 * In general we want to start rendering lines one bar sooner than what is visible to user and stop rendering
 * one bar outside of visible area to create a continuous line.
 */
import { ScaleAxis } from '../models/pane'
import type Quote from '../models/quote'

export const drawInVisibleArea = ({
  quote,
  scale,
  fxOverride,
  fromIndexOffset = 0,
  toIndexOffset = 0,
  leftOffset,
  drawBarCallback,
  width,
}: {
  quote: Quote
  scale: { x: ScaleAxis }
  fromIndexOffset?: number
  toIndexOffset?: number
  fxOverride?: (index: number) => number
  leftOffset: number
  width: number
  drawBarCallback: (i: number, x: number) => void
}) => {
  const fx = fxOverride ?? ((index: number) => scale.x(quote.barIndex[index]))
  // We want to render one bar before first visible bar to fix disconnected lines on charts with missing data
  const firstBarToRender = Math.max(0, getFirstBarToRenderIndex({ quote, scale, leftOffset }) - 1, fromIndexOffset - 1)

  let lastBarToRender = -1
  for (let i = firstBarToRender; i < quote.close.length + toIndexOffset; i++) {
    const x = fx(i)
    if (x + leftOffset > width) {
      lastBarToRender = i
      break
    }
    drawBarCallback(i, x)
  }

  if (lastBarToRender > -1) {
    const x = fx(lastBarToRender)
    drawBarCallback(lastBarToRender, x)
  }
}

export const getFirstBarToRenderIndex = ({
  leftOffset,
  quote,
  scale,
}: {
  quote: Quote
  scale: { x: ScaleAxis }
  leftOffset: number
}) => {
  const barIndex = Math.ceil(scale.x.invert(-leftOffset))
  const calculatedIndex = quote.barToDataIndex[Math.max(barIndex, 0)]
  // not all the time there is bar present on barIndex calculated via inverted leftOffset
  // there might be a gap between bars, if there is, barToDataIndex on that gap spot will have value of
  // previous bar which isn't visible, because of that we need to check if that happend
  // and correct it if so by increasing calculated index by 1
  return scale.x(quote.barIndex[calculatedIndex]) + leftOffset >= 0 ? calculatedIndex : calculatedIndex + 1
}

export const getLastBarToRenderIndex = ({
  leftOffset,
  quote,
  scale,
  chartWidth,
}: {
  quote: Quote
  scale: { x: ScaleAxis }
  leftOffset: number
  chartWidth: number
}) => {
  const { barToDataIndex } = quote
  const barIndex = Math.ceil(scale.x.invert(-leftOffset + chartWidth))
  return barToDataIndex[Math.max(barIndex, 0)] ?? barToDataIndex[barToDataIndex.length - 1]
}
